/**
 * EVC Borrow Dialog
 *
 * This dialog uses Euler's full wagmi-evc stack for borrow operations.
 */

import { useWrongNetwork } from '@/domain/hooks/useWrongNetwork'
import { CommonDialogProps, DialogConfig } from '@/features/dialogs/common/types'
import { Dialog, DialogContent } from '@/ui/atoms/dialog/Dialog'
import { type Address } from 'viem'
import { EvcBorrowDialogContentContainer } from './EvcBorrowDialogContentContainer'

export interface EvcBorrowDialogProps extends CommonDialogProps {
  collateralVault: Address
  collateralSymbol: string
  collateralAmount: string
  collateralDecimals: number
  collateralAsset: Address
  collateralLogoURI?: string
  liabilityVault: Address
  liabilitySymbol: string
  borrowAmount: string
  liabilityDecimals: number
  liabilityAsset: Address
  liabilityLogoURI?: string
  /** Sub-account ID (0-255). 0 = main account. */
  subAccountId?: number
  /** If true, wrap native HYPE → WHYPE for collateral deposit */
  wrapCollateralFromNative?: boolean
  /** If true, unwrap WHYPE → native HYPE after borrow */
  unwrapBorrowToNative?: boolean
  /** Existing controller to disable before enabling new one (for controller switching) */
  existingController?: Address
  /** Whether we're modifying an existing position (affects dialog title) */
  isModifyingExistingPosition?: boolean
  /** List of collateral vaults already enabled for this position (to skip redundant enableCollateral calls) */
  enabledCollaterals?: Address[]
}

function EvcBorrowDialog({
  open,
  setOpen,
  collateralVault,
  collateralSymbol,
  collateralAmount,
  collateralDecimals,
  collateralAsset,
  collateralLogoURI,
  liabilityVault,
  liabilitySymbol,
  borrowAmount,
  liabilityDecimals,
  liabilityAsset,
  liabilityLogoURI,
  subAccountId,
  wrapCollateralFromNative,
  unwrapBorrowToNative,
  existingController,
  isModifyingExistingPosition,
  enabledCollaterals,
}: EvcBorrowDialogProps) {
  const isWrongNetwork = useWrongNetwork()

  if (isWrongNetwork) {
    setOpen(false)
    return null
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent aria-describedby={undefined}>
        <EvcBorrowDialogContentContainer
          closeDialog={() => setOpen(false)}
          collateralVault={collateralVault}
          collateralSymbol={collateralSymbol}
          collateralAmount={collateralAmount}
          collateralDecimals={collateralDecimals}
          collateralAsset={collateralAsset}
          collateralLogoURI={collateralLogoURI}
          liabilityVault={liabilityVault}
          liabilitySymbol={liabilitySymbol}
          borrowAmount={borrowAmount}
          liabilityDecimals={liabilityDecimals}
          liabilityAsset={liabilityAsset}
          liabilityLogoURI={liabilityLogoURI}
          subAccountId={subAccountId}
          wrapCollateralFromNative={wrapCollateralFromNative}
          unwrapBorrowToNative={unwrapBorrowToNative}
          existingController={existingController}
          isModifyingExistingPosition={isModifyingExistingPosition}
          enabledCollaterals={enabledCollaterals}
        />
      </DialogContent>
    </Dialog>
  )
}

export const evcBorrowDialogConfig: DialogConfig<EvcBorrowDialogProps> = {
  options: {
    closeOnChainChange: true,
  },
  element: EvcBorrowDialog,
}

export default EvcBorrowDialog
